// components
import { Button } from '@mui/material';
import { Drawer } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { NotesSection } from './form/NotesSection';
import { PreferencesSection } from './form/PreferencesSection';
import { QualificationSection } from './form/QualificationSection';
import { RequestSection } from './form/RequestSection';

// constants
import { FLEX_REQUEST_FORM_DEFAULT_VALUES } from './NewFlexRequestDrawer.consts';
import { FILTER_CACHE_TIME } from './FlexMatchFilters.consts';

// hooks
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useGetRequestReason from 'components/flex-portal/FlexHistory/hooks/useGetRequestReason';
import useGetRosterFunctions from 'hooks/useGetRosterFunctions';
import useAddFlexMatchRequest from '../hooks/useAddFlexMatchRequest';

// queries & mutations
import { useGetQualifications } from 'queries';

// schema
import { newFlexRequestSchema } from './NewFlexRequestDrawer.schema';

// types
import { PreferredEmployee } from 'components/shared/PreferredEmployees.types';
import { NewFlexRequestFormValues } from './NewFlexRequestDrawer.types';
import { DutyLineNote } from 'RestAPIs/employees/hours-table/types';

// utils
import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';

const NewFlexRequestDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch.newFlexRequestDrawer'
  });
  const { t: tCommon } = useTranslation('common');
  const [draftNotes, setDraftNotes] = useState<DutyLineNote[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<
    PreferredEmployee[]
  >([]);

  const { data: requestReasonList } = useGetRequestReason({
    ...FILTER_CACHE_TIME,
    enabled: !!isOpen
  });

  const methods = useForm<NewFlexRequestFormValues>({
    defaultValues: FLEX_REQUEST_FORM_DEFAULT_VALUES,
    resolver: yupResolver(newFlexRequestSchema)
  });

  const { handleSubmit, watch, reset, setValue } = methods;
  const selectedDate = watch('date');

  const referenceDate = selectedDate
    ? dayjs(selectedDate).startOf('day').toDate()
    : undefined;

  const { data: rosterFunctionOptions } = useGetRosterFunctions({
    ...FILTER_CACHE_TIME,
    enabled: !!isOpen
  });
  const { data: qualificationOptions } = useGetQualifications({
    enabled: !!isOpen
  });
  const addFlexMatchRequest = useAddFlexMatchRequest();
  const { isLoading: isAddingRequest } = addFlexMatchRequest;

  const handleClose = () => {
    onClose?.();
    setDraftNotes([]);
    setSelectedEmployees([]);
    reset(FLEX_REQUEST_FORM_DEFAULT_VALUES);
  };

  const onSubmit = (data: NewFlexRequestFormValues) => {
    if (!data) return;

    const {
      reason,
      date,
      costCenter,
      rosterFunction,
      dutyType,
      preferences,
      qualifications
    } = data;

    if (
      !reason ||
      !date ||
      !costCenter ||
      !rosterFunction ||
      !dutyType ||
      !preferences.length ||
      !qualifications.length
    ) {
      return;
    }

    const payload = {
      costCenterId: costCenter?.id,
      functionId: rosterFunction?.id,
      dutyId: dutyType?.dutyHourTypeId,
      date: dayjs(date).startOf('day').toDate().toISOString(),
      reason: reason?.code,
      notes: draftNotes,
      qualifications: qualifications.map(({ id }) => ({
        qualificationId: id
      })),
      preferredEmployees: preferences?.map((employee) => ({
        employeeId: employee.employeeId,
        createdBy: employee.createdBy
      }))
    };

    addFlexMatchRequest.mutate(payload, {
      onSuccess: () => {
        handleClose();
      }
    });
  };

  useEffect(() => {
    setValue('preferences', selectedEmployees);
  }, [selectedEmployees]);

  return (
    <Drawer
      open={isOpen}
      title={t('title')}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: {
            compact: '400px'
          }
        }
      }}
    >
      <FormProvider {...methods}>
        <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
          <RequestSection
            requestReasonList={requestReasonList}
            rosterFunctionOptions={rosterFunctionOptions}
            referenceDate={referenceDate}
            selectedDate={selectedDate}
          />
          <PreferencesSection
            selectedDate={selectedDate}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
          />
          <QualificationSection qualificationOptions={qualificationOptions} />
          <NotesSection draftNotes={draftNotes} setDraftNotes={setDraftNotes} />
          <MultiStepForm.Actions>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => {
                handleClose();
              }}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              loading={isAddingRequest}
              type="submit"
              variant="contained"
              color="primary"
            >
              {tCommon('save')}
            </Button>
          </MultiStepForm.Actions>
        </MultiStepForm>
      </FormProvider>
    </Drawer>
  );
};

export default NewFlexRequestDrawer;
